import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Shield, ShieldCheck, ShieldOff, Smartphone, Key, Copy, Check,
  RefreshCw, AlertTriangle, CheckCircle2, XCircle, Clock,
  Lock, Unlock, History, Download, QrCode
} from 'lucide-react';
import { api } from '@/lib/api';

interface TwoFactorStatus {
  isEnabled: boolean;
  enabledAt: string | null;
  lastUsedAt: string | null;
  recoveryCodesRemaining: number;
}

interface TwoFactorLog {
  id: number;
  action: string;
  success: boolean;
  ipAddress: string | null;
  errorMessage: string | null;
  createdAt: string;
}

interface UserInfo {
  userId: string;
  id: string;
  email: string;
  userName: string;
}

export default function TwoFactorPage() {
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [logs, setLogs] = useState<TwoFactorLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState<{secretKey: string; qrCodeUrl: string} | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [showSetup, setShowSetup] = useState(false);
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      loadStatus();
      loadLogs();
    }
  }, [selectedUserId]);

  const loadUsers = async () => {
    try {
      const res = await api.get('/api/users?pageSize=100');
      if (res.data.success) {
        const data = res.data.data;
        const usersList = Array.isArray(data) ? data : (data?.items || []);
        setUsers(usersList);
        if (usersList.length > 0 && !selectedUserId) {
          setSelectedUserId(usersList[0].userId);
        }
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadStatus = async () => {
    try {
      const res = await api.get(`/api/twofactor/status/${selectedUserId}`);
      if (res.data.success) setStatus(res.data.data);
    } catch (error) {
      console.error('Error loading status:', error);
    }
  };

  const loadLogs = async () => {
    try {
      const res = await api.get(`/api/twofactor/logs/${selectedUserId}`);
      if (res.data.success) setLogs(res.data.data);
    } catch (error) {
      console.error('Error loading logs:', error);
    }
  };

  const setupTwoFactor = async () => {
    if (!selectedUserId) {
      alert('يرجى اختيار مستخدم أولاً');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post(`/api/twofactor/setup/${selectedUserId}`);
      if (res.data.success) {
        setSetupData(res.data.data);
        setShowSetup(true);
      } else {
        alert(res.data.message || 'فشل في إنشاء المفتاح السري');
      }
    } catch (error) {
      console.error('Error setting up 2FA:', error);
      const errorMsg = (error as any).response?.data?.message || 'حدث خطأ أثناء إعداد 2FA. تأكد من وجود المستخدم في قاعدة البيانات';
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const enableTwoFactor = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      alert('يرجى إدخال كود صحيح من 6 أرقام');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post(`/api/twofactor/enable/${selectedUserId}`, { code: verificationCode });
      if (res.data.success) {
        setRecoveryCodes(res.data.data.recoveryCodes);
        setShowRecoveryCodes(true);
        setShowSetup(false);
        setVerificationCode('');
        loadStatus();
        loadLogs();
        alert('تم تفعيل المصادقة الثنائية بنجاح');
      } else {
        alert(res.data.message || 'كود التحقق غير صحيح');
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'حدث خطأ');
    } finally {
      setLoading(false);
    }
  };

  const disableTwoFactor = async () => {
    if (!confirm('هل أنت متأكد من تعطيل المصادقة الثنائية؟ سيصبح الحساب أقل أماناً.')) return;
    setLoading(true);
    try {
      const res = await api.post(`/api/twofactor/disable/${selectedUserId}`);
      if (res.data.success) {
        alert('تم تعطيل المصادقة الثنائية بنجاح');
        loadStatus();
        loadLogs();
      }
    } catch (error) {
      console.error('Error disabling 2FA:', error);
    } finally {
      setLoading(false);
    }
  };

  const regenerateRecoveryCodes = async () => {
    if (!confirm('هل أنت متأكد؟ سيتم إلغاء الرموز القديمة.')) return;
    try {
      const res = await api.post(`/api/twofactor/regenerate-recovery-codes/${selectedUserId}`);
      if (res.data.success) {
        setRecoveryCodes(res.data.data.recoveryCodes);
        setShowRecoveryCodes(true);
        loadStatus();
      }
    } catch (error) {
      console.error('Error regenerating codes:', error);
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const downloadRecoveryCodes = () => {
    const content = `Target Express - رموز الاستعادة\n\n${recoveryCodes.join('\n')}\n\nاحفظ هذه الرموز في مكان آمن. ستحتاجها إذا فقدت الوصول إلى تطبيق المصادقة.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'recovery-codes.txt';
    a.click();
  };

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      'generate_secret': 'إنشاء مفتاح سري',
      'verify_code': 'التحقق من الكود',
      'enable_2fa': 'تفعيل 2FA',
      'disable_2fa': 'تعطيل 2FA',
      'generate_recovery_codes': 'إنشاء رموز استعادة',
      'verify_recovery_code': 'استخدام رمز استعادة'
    };
    return labels[action] || action;
  };

  const selectedUser = users.find(u => u.id === selectedUserId);

  return (
    <div className="p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            المصادقة الثنائية (2FA)
          </h1>
          <p className="text-muted-foreground mt-1">تعزيز أمان حسابات المستخدمين بالمصادقة الثنائية</p>
        </div>
        <select
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}
          className="px-4 py-2 border rounded-lg bg-white"
        >
          {users.map(u => (
            <option key={u.id} value={u.userId}>{u.email || u.userName} (#{u.userId})</option>
          ))}
        </select>
      </div>

      {/* Status Card */}
      {status && (
        <Card className={`border-2 ${status.isEnabled ? 'border-emerald-500 bg-gradient-to-br from-emerald-50 to-teal-50' : 'border-gray-200'}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                  status.isEnabled 
                    ? 'bg-gradient-to-br from-emerald-500 to-teal-600' 
                    : 'bg-gray-200'
                }`}>
                  {status.isEnabled ? 
                    <ShieldCheck className="w-8 h-8 text-white" /> : 
                    <ShieldOff className="w-8 h-8 text-gray-500" />
                  }
                </div>
                <div>
                  <h2 className="text-xl font-bold">
                    {status.isEnabled ? 'المصادقة الثنائية مفعلة' : 'المصادقة الثنائية معطلة'}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedUser?.email || selectedUser?.userName}
                  </p>
                  {status.isEnabled && status.enabledAt && (
                    <p className="text-xs text-gray-500 mt-1">
                      مفعل منذ: {new Date(status.enabledAt).toLocaleDateString('ar-EG')}
                    </p>
                  )}
                </div>
              </div>
              <div>
                {status.isEnabled ? (
                  <Button onClick={disableTwoFactor} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 gap-2">
                    <ShieldOff className="w-4 h-4" />
                    تعطيل 2FA
                  </Button>
                ) : (
                  <Button onClick={setupTwoFactor} className="bg-emerald-600 hover:bg-emerald-700 gap-2" disabled={loading}>
                    <ShieldCheck className="w-4 h-4" />
                    تفعيل 2FA
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      {status && status.isEnabled && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">رموز الاستعادة المتبقية</p>
                  <p className="text-2xl font-bold">{status.recoveryCodesRemaining}</p>
                </div>
                <Key className="w-8 h-8 text-amber-500" />
              </div>
              <Button 
                onClick={regenerateRecoveryCodes} 
                variant="outline" 
                size="sm" 
                className="mt-2 w-full gap-2"
              >
                <RefreshCw className="w-3 h-3" />
                إعادة إنشاء
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">تاريخ التفعيل</p>
                  <p className="text-lg font-bold">
                    {status.enabledAt ? new Date(status.enabledAt).toLocaleDateString('ar-EG') : '-'}
                  </p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">آخر استخدام</p>
                  <p className="text-lg font-bold">
                    {status.lastUsedAt ? new Date(status.lastUsedAt).toLocaleDateString('ar-EG') : 'لم يستخدم بعد'}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-blue-500" />
            سجل المصادقة الثنائية
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {logs.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">لا يوجد سجلات</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-right p-3 font-semibold">الإجراء</th>
                    <th className="text-right p-3 font-semibold">الحالة</th>
                    <th className="text-right p-3 font-semibold">عنوان IP</th>
                    <th className="text-right p-3 font-semibold">التاريخ</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{getActionLabel(log.action)}</td>
                      <td className="p-3">
                        <Badge className={log.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {log.success ? <CheckCircle2 className="w-3 h-3 ml-1" /> : <XCircle className="w-3 h-3 ml-1" />}
                          {log.success ? 'نجاح' : 'فشل'}
                        </Badge>
                      </td>
                      <td className="p-3 text-sm text-gray-600">{log.ipAddress || '-'}</td>
                      <td className="p-3 text-sm text-gray-500">
                        {new Date(log.createdAt).toLocaleString('ar-EG')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Setup Modal */}
      {showSetup && setupData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-emerald-500" />
                إعداد المصادقة الثنائية
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-900">تحذير مهم</p>
                    <p className="text-sm text-amber-700 mt-1">
                      امسح رمز QR أدناه باستخدام تطبيق Google Authenticator أو Microsoft Authenticator.
                      إذا لم تستطع مسح الرمز، أدخل المفتاح السري يدوياً.
                    </p>
                  </div>
                </div>
              </div>

              <div className="text-center p-6 bg-white border-2 border-dashed rounded-lg">
                <QrCode className="w-48 h-48 mx-auto mb-4" />
                <p className="text-sm text-gray-500 mb-2">امسح رمز QR باستخدام تطبيق المصادقة</p>
                <p className="text-xs text-gray-400">(الرمز يتطلب مكتبة QR لعرضه)</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">المفتاح السري (للإدخال اليدوي)</label>
                <div className="flex items-center gap-2">
                  <Input 
                    value={setupData.secretKey} 
                    readOnly 
                    className="font-mono bg-gray-50"
                  />
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => copyToClipboard(setupData.secretKey, 'secret')}
                  >
                    {copied === 'secret' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">أدخل الكود من التطبيق *</label>
                <Input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  maxLength={6}
                  className="text-center text-2xl font-mono tracking-widest"
                />
                <p className="text-xs text-gray-500 mt-1">أدخل الكود الذي يتكون من 6 أرقام من التطبيق</p>
              </div>

              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={enableTwoFactor} 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  disabled={loading || verificationCode.length !== 6}
                >
                  {loading ? 'جاري التحقق...' : 'تأكيد وتفعيل'}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => { setShowSetup(false); setSetupData(null); setVerificationCode(''); }} 
                  className="flex-1"
                >
                  إلغاء
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recovery Codes Modal */}
      {showRecoveryCodes && recoveryCodes.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5 text-amber-500" />
                رموز الاستعادة
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-900">احفظ هذه الرموز!</p>
                    <p className="text-sm text-amber-700 mt-1">
                      يمكنك استخدام هذه الرموز للوصول إلى حسابك إذا فقدت الوصول إلى تطبيق المصادقة.
                      كل رمز يمكن استخدامه مرة واحدة فقط.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {recoveryCodes.map((code, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-50 border rounded-lg p-3">
                    <code className="font-mono text-sm font-bold">{code}</code>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(code, `code-${i}`)}
                      className="h-6 w-6 p-0"
                    >
                      {copied === `code-${i}` ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                    </Button>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 pt-4">
                <Button onClick={downloadRecoveryCodes} className="flex-1 gap-2">
                  <Download className="w-4 h-4" />
                  تحميل الرموز
                </Button>
                <Button 
                  onClick={() => copyToClipboard(recoveryCodes.join('\n'), 'all')}
                  variant="outline" 
                  className="flex-1 gap-2"
                >
                  {copied === 'all' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  نسخ الكل
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => { setShowRecoveryCodes(false); setRecoveryCodes([]); }}
                  className="flex-1"
                >
                  إغلاق
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
